import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import some from 'lodash/some';
import includes from 'lodash/includes';
import sortBy from 'lodash/sortBy';
import moment from 'moment';
import APIUtils from '@spothero/utils/api';
import FormatUtils from 'utils/format';
import Config from 'config';
import FacilityAPI from 'api/facility';

const SpotUtils = {
    airportSortValues: {
        ALPHABETICAL: 'alphabetical',
        PRICE: 'price',
        FEATURED: 'featured',
        SHUTTLE_TIME: 'shuttleTime',
    },

    formatAccessHours: accessHours => {
        const accessHoursFormatted = [];
        let alwaysOpen = false;
        let accessHoursTmp = accessHours;

        // if accessHours contain text and periods, evaluate text
        // and update accessHours to reflect accessHours.periods
        if (accessHoursTmp.text) {
            if (
                accessHoursTmp.text.length &&
                accessHoursTmp.text[0] === 'This facility is open 24/7.'
            ) {
                alwaysOpen = true;
            }

            accessHoursTmp = accessHoursTmp.periods;
        }

        if (!alwaysOpen && accessHoursTmp.length) {
            const {
                start_dow_int: startDowInt,
                end_dow_int: endDowInt,
                start_time: startTime,
                end_time: endTime,
            } = accessHoursTmp[0];

            if (startDowInt === endDowInt && startTime === endTime) {
                alwaysOpen = true;
            }
        }

        if (alwaysOpen) {
            accessHoursFormatted.push({
                type: '247',
            });

            return accessHoursFormatted;
        }

        forEach(accessHoursTmp, period => {
            let days = period.start_dow;
            let value;

            if (period.start_dow !== period.end_dow) {
                days = `${period.start_dow} - ${period.end_dow}`;
            }

            if (period.start_time && period.end_time) {
                const inFormat = 'YYYY-MM-DD HH:mm:ss';
                const outFormat = 'h:mma';

                value = `${moment(
                    `2025-01-01 ${period.start_time}`,
                    inFormat
                ).format(outFormat)} - ${moment(
                    `2025-01-01 ${period.end_time}`,
                    inFormat
                ).format(outFormat)}`;
            } else if (period.hours_type) {
                value = period.hours_type;
            }

            accessHoursFormatted.push({
                days,
                value,
                type: period.hours_type,
            });
        });

        return accessHoursFormatted;
    },

    buildCheckoutURL(spot, rate, searchRequest) {
        const rebookId = searchRequest.rebook_reservation_id; // eslint-disable-line camelcase
        let spotUrl = spot.spot_url || spot.facility.facility_url;

        if (searchRequest.monthly) {
            spotUrl += `?rid=${rate.rule_id}&starts=${searchRequest.starts}&monthly=true`;
        } else {
            if (!isNil(searchRequest.eid)) {
                spotUrl = !includes(rate.url, 'eid')
                    ? `${rate.url}&eid=${searchRequest.eid}`
                    : rate.url;
            } else {
                spotUrl += `?rid=${rate.rule_group_id}&starts=${searchRequest.starts}&ends=${searchRequest.ends}`;

                if (searchRequest.airport) {
                    spotUrl += `&airport=true`;
                }
            }
        }

        if (rebookId) {
            if (includes(spotUrl, '?')) {
                spotUrl += `&rebook_reservation_id=${rebookId}`;
            } else {
                spotUrl += `?rebook_reservation_id=${rebookId}`;
            }
        }

        const spotUrlArr = spotUrl.split('/');

        if (includes(spotUrlArr[0], 'http')) {
            spotUrl = spotUrlArr.slice(3).join('/');
        }

        return `/${spotUrl}`;
    },

    prepare({spot, searchRequest, timezone}) {
        const facility =
            spot.facility && spot.facility.id ? spot.facility : spot;
        /* eslint-disable camelcase */
        const preparedResult = {
            ...spot,
            spotId: facility.parking_spot_id || facility.id,
            hourly_rates: spot.hourly_rates || [],
            monthly_rates: spot.monthly_rates || [],
            activeMarker: false,
            isFavorite: false,
            hasTravelDistanceUpdated: false,
            hidden: false,
            selectedRate: null,
            url: spot.spot_url,
            hasOversizedFee:
                facility.vehicle_size === 'oversized_with_fee' || false,
            oversizedFeeMessage: facility.oversize_description || null,
            vehicleRestriction:
                facility.vehicle_restriction_description || null,
            facility: {
                ...facility,
                access_hours_formatted: this.formatAccessHours(
                    facility.hours_of_operation
                ),
            },
        };
        /* eslint-enable camelcase */
        let currentRate;

        // prepare hourly rates
        forEach(preparedResult.hourly_rates, (rate, index) => {
            currentRate = preparedResult.hourly_rates[index];

            if (isNil(currentRate.hidden)) {
                currentRate.hidden = false;
            }

            // is this a recently updated monthly rate? - BK 4/1/2015
            currentRate.newMonthlyRate = false;

            // url
            currentRate.fullUrl = SpotUtils.buildCheckoutURL(
                spot,
                currentRate,
                searchRequest
            );

            // starts & ends: remove timezone info to conform datetimes to `YYYY-MM-DDTHH:MM` format
            currentRate.starts = moment
                .tz(currentRate.starts, timezone)
                .format(Config.apiDateTimeFormat);

            currentRate.ends = moment
                .tz(currentRate.ends, timezone)
                .format(Config.apiDateTimeFormat);

            // access hours
            /* eslint-disable camelcase */
            if (
                preparedResult.facility.access_hours_formatted &&
                preparedResult.facility.access_hours_formatted.length
            ) {
                currentRate.access_hours_formatted =
                    preparedResult.facility.access_hours_formatted;
            } else {
                currentRate.access_hours_formatted = false;
            }
            /* eslint-enable camelcase */

            // full rule id
            currentRate.fullRule = currentRate.rule_group_id;
        });

        // prepare monthly rates
        forEach(preparedResult.monthly_rates, (rate, index) => {
            currentRate = preparedResult.monthly_rates[index];

            if (isNil(currentRate.hidden)) {
                currentRate.hidden = false;
            }

            // is this a recently updated monthly rate? - BK 4/1/2015
            if (
                !isNil(currentRate.redemption_instructions) &&
                currentRate.redemption_instructions.length
            ) {
                currentRate.newMonthlyRate = true;
            } else {
                currentRate.newMonthlyRate = false;
            }

            // url
            currentRate.fullUrl = SpotUtils.buildCheckoutURL(
                spot,
                currentRate,
                searchRequest
            );

            // amenities
            // only update if result contains facility otherwise we assume its already present
            // TODO this is a fallback for monthly rates that have not yet been updated at the rate level
            if (!currentRate.newMonthlyRate) {
                currentRate.amenities = spot.facility.amenities_full;
            } else {
                currentRate.amenities = currentRate.amenities_full;
            }

            // access hours
            /* eslint-disable camelcase */
            if (currentRate.access_hours.length) {
                currentRate.access_hours_formatted = this.formatAccessHours(
                    currentRate.access_hours
                ); // eslint-disable-line camelcase
            } else {
                if (spot.facility.hours_of_operation.periods.length) {
                    currentRate.access_hours_formatted =
                        preparedResult.facility.access_hours_formatted; // eslint-disable-line camelcase
                } else {
                    currentRate.access_hours_formatted = false; // eslint-disable-line camelcase
                }
            }
            /* eslint-enable camelcase */

            // full rule id
            currentRate.fullRule = currentRate.rule_id;
        });

        // set selectedRate
        if (
            isNil(preparedResult.selectedRate) ||
            !preparedResult.selectedRate
        ) {
            preparedResult.selectedRate = null;

            if (searchRequest.monthly) {
                if (preparedResult.monthly_rates.length) {
                    preparedResult.selectedRate = cloneDeep(
                        preparedResult.monthly_rates[0]
                    );
                } else {
                    preparedResult.selectedRate = null;
                }
            } else {
                if (preparedResult.hourly_rates.length) {
                    preparedResult.selectedRate = cloneDeep(
                        preparedResult.hourly_rates[0]
                    );
                } else {
                    preparedResult.selectedRate = null;
                }
            }
        }

        return preparedResult;
    },

    filter(spots, filterState, isMonthly) {
        const {
            activeReservationType,
            activeAmenities,
            price,
            reservationTypes,
        } = filterState;
        const relatedReservationTypes =
            reservationTypes[activeReservationType].related;
        const newFiltered = [];

        forEach(spots, spot => {
            const rateType = isMonthly ? 'monthly_rates' : 'hourly_rates';
            const rates = spot[rateType];
            let show = true;

            if (price && spot.selectedRate.price > price) {
                show = false;
            }

            // default rates to hidden = false
            forEach(rates, rate => {
                rate.hidden = false;
            });

            // filter monthly results by reservationType
            if (show && isMonthly && activeReservationType !== 'all') {
                let hasMatchingRate = false;

                forEach(rates, rate => {
                    // set selectedRate if type matches
                    // if not set as hidden (from filters)
                    if (
                        !hasMatchingRate &&
                        rate.reservation_type === activeReservationType
                    ) {
                        spot.selectedRate = rate;
                        hasMatchingRate = true;
                    }

                    // attempt to match related rates if neccesary
                    if (!hasMatchingRate) {
                        forEach(
                            relatedReservationTypes,
                            relatedReservationType => {
                                if (
                                    rate.reservation_type ===
                                    relatedReservationType
                                ) {
                                    rate.hidden = false;
                                    spot.selectedRate = rate;
                                    hasMatchingRate = true;
                                }
                            }
                        );
                    }
                });

                show = hasMatchingRate;
            }

            if (show && activeAmenities.length) {
                let hasMatchingRate = false;

                forEach(rates, rate => {
                    // filter by active amenities
                    const rateMatches = [];

                    forEach(activeAmenities, amenity => {
                        if (includes(amenity, 'no-')) {
                            if (
                                !some(rate.amenities, {
                                    slug: amenity.replace('no-', ''),
                                })
                            ) {
                                rateMatches.push(amenity);
                            }
                        } else {
                            if (some(rate.amenities, {slug: amenity})) {
                                rateMatches.push(amenity);
                            }
                        }
                    });

                    if (rateMatches.length === activeAmenities.length) {
                        hasMatchingRate = true;
                    } else {
                        rate.hidden = true;
                    }
                });

                show = hasMatchingRate;
            }

            spot[rateType] = rates;

            if (show) {
                spot.hidden = false;
                newFiltered.push(spot);
            } else {
                spot.hidden = true;
            }
        });

        return newFiltered;
    },

    sortByAttribute(spots, attr = 'distance') {
        const comparator = {
            [this.airportSortValues.ALPHABETICAL]: spot => spot.title,
            [this.airportSortValues.PRICE]: spot =>
                spot.selectedRate.display_price,
            [this.airportSortValues.FEATURED]: spot => spot.facility.order,
            [this.airportSortValues.SHUTTLE_TIME]: spot =>
                spot.facility.airport.shuttle.duration,
        };

        return sortBy(spots, [comparator[attr] || attr]);
    },

    prepareGroup({spots, searchRequest, timezone}) {
        return map(spots, spot => {
            return SpotUtils.prepare({
                spot,
                searchRequest,
                timezone,
            });
        });
    },

    loadRates({
        spotId,
        starts,
        ends,
        isMonthly,
        searchDataParams,
        timezone,
        spot = null,
        eventId = null,
        onSuccessWithRates,
        onSuccessWithoutRates,
        onError,
    }) {
        const rateLoader = APIUtils.makeCancelable(
            FacilityAPI.getRates({
                spotId,
                starts,
                ends,
                eventId,
                monthly: isMonthly,
                includeFacility: true,
            })
        );

        rateLoader.promise
            .then(resp => {
                const spotData = resp.data.data;
                const rateType = isMonthly ? 'monthly_rates' : 'hourly_rates';

                if (spotData[rateType].length > 0) {
                    // TODO: BK - is there another way to better handle this?
                    // need to assign updated starts and ends to params on state in this case because
                    // updateSearchTimes is not called until the callback to first ensure time changes are valid
                    const newSpotData = SpotUtils.prepare({
                        spot: spotData,
                        searchRequest: {
                            ...searchDataParams,
                            starts,
                            ends,
                        },
                        timezone,
                    });
                    // MP: When loading rates from a `sid` on page load, no spot data will be provided to this method so
                    // we just want to use the one that comes back from the response so that we have spot data to show
                    // the spot details properly on the search page
                    const savedSpotData = spot ? spot : newSpotData;
                    let newRate = newSpotData.selectedRate;

                    if (
                        spotData[rateType].length > 1 &&
                        spot &&
                        spot.selectedRate
                    ) {
                        // if there are multiple rates make sure to select the same rate that was selected pre-reloading
                        // so that the UI matches up correctly and the proper rate is selected in the choices
                        const priorMatchedRate = find(newSpotData[rateType], {
                            fullRule: spot.selectedRate.fullRule,
                        });

                        if (priorMatchedRate) {
                            newRate = priorMatchedRate;
                        }
                    }

                    if (onSuccessWithRates) {
                        /* eslint-disable camelcase */
                        onSuccessWithRates({
                            ...savedSpotData,
                            hourly_rates: newSpotData.hourly_rates,
                            monthly_rates: newSpotData.monthly_rates,
                            lowest_monthly_price:
                                newSpotData.lowest_monthly_price,
                            selectedRate: newRate,
                        });
                        /* eslint-disable camelcase */
                    }
                } else {
                    if (onSuccessWithoutRates) {
                        onSuccessWithoutRates();
                    }
                }
            })
            .catch(error => {
                if (onError) {
                    onError(error);
                }
            });

        return rateLoader;
    },

    formatRatePrice({price, currencyType}) {
        const {dollars, cents} = FormatUtils.price(price);

        return `<sup>${FormatUtils.currencySymbol(
            currencyType
        )}</sup>${dollars}${cents ? `<sup>.${cents}</sup>` : ''}`;
    },
};

export default SpotUtils;
